import React, { useState, useEffect, useCallback } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import SchedulerDetail from '../../../components/Scheduler/SchedulerDetail';
import { getData, putData } from '../../../utils/httpClient';

const Scheduler = () => {
  const [appointmentList, setAppointmentList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [vehicleService, setVehicleService] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mechanicsList, setMechanicsList] = useState([]);
  const [vehicleIdsWithAlerts, setVehicleIdsWithAlerts] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  const toast = useToast();

  const loaderOpts = { skipLoader: true };

  const getHolidays = async () => {
    try {
      const response = await getData('/holidays', loaderOpts);
      setHolidayList(response.payload.holidays);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los feriados.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getMechanics = async () => {
    try {
      const response = await getData('/users/mechanics', loaderOpts);
      setMechanicsList(response?.payload?.mechanics || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los operarios.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getAppointments = useCallback(async (from, to) => {
    if (!from || !to) return;
    try {
      const response = await getData(
        `/agenda?from=${from}&to=${to}`,
        loaderOpts,
      );
      setAppointmentList(response?.payload?.agendas || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las agendas.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El calendario informa qué rango de fechas necesita según la fecha/vista activa
  // (día, semana, mes) y navegación (hoy/anterior/siguiente); solo se pide esa porción.
  const handleAppointmentRangeChange = useCallback(
    (from, to) => {
      setDateRange({ from, to });
      getAppointments(from, to);
    },
    [getAppointments],
  );

  const refreshAppointments = useCallback(
    () => getAppointments(dateRange?.from, dateRange?.to),
    [getAppointments, dateRange],
  );

  const getStatus = async () => {
    try {
      const response = await getData('/status', loaderOpts);
      setStatusList(response.payload.status);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los estados.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getProducts = async () => {
    try {
      const response = await getData('/products?page=1&limit=500', loaderOpts);
      setProductsList(response.payload.products);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los productos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadVehicleAlerts = async () => {
    try {
      const res = await getData('/vehicleAlerts/vehicles/active', loaderOpts);
      setVehicleIdsWithAlerts(res?.payload?.vehicleIds || []);
    } catch {
      setVehicleIdsWithAlerts([]);
    }
  };

  useEffect(() => {
    // La carga inicial de citas la dispara SchedulerCalendar al montar,
    // vía onAppointmentRangeChange, con el rango de la vista activa.
    getHolidays();
    getStatus();
    getProducts();
    getMechanics();
    loadVehicleAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState === 'visible') {
        refreshAppointments();
      }
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [refreshAppointments]);

  const putVehicleService = async (vehicleServiceData) => {
    const serviceId = Number(vehicleServiceData?.id);
    if (!Number.isFinite(serviceId) || serviceId <= 0) {
      const msg =
        'No se encontró el servicio del vehículo. Recargá la agenda o contactá soporte.';
      toast({
        title: 'Servicio no vinculado',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw new Error(msg);
    }
    try {
      const response = await putData(
        `/vehicleServices/${serviceId}`,
        { ...vehicleServiceData, id: serviceId },
      );
      setVehicleService(response.payload.vehicleService);
      return response.payload.vehicleService;
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo actualizar el servicio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const putDynamicTest = async (vehicleServiceId, payload) => {
    try {
      const response = await putData(
        `/vehicleServices/${vehicleServiceId}/dynamic-test`,
        payload,
      );
      setVehicleService(response.payload.vehicleService);
      return response.payload.vehicleService;
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo guardar la prueba dinámica.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const putVehicleData = async (vehicle) => {
    try {
      await putData(`/vehicles/${vehicle.id}`, vehicle);
      setAppointmentList((prev) =>
        prev.map((appointment) =>
          appointment.vehicle?.id === vehicle.id
            ? { ...appointment, vehicle }
            : appointment,
        ),
      );
      toast({
        title: 'Vehículo actualizado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el vehículo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const putAppointment = async (appointment) => {
    try {
      const response = await putData(`/agenda/${appointment.id}`, appointment);
      return response.payload.agenda;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la agenda.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const linkAgendaToVehicleService = async (appointment, vehicleServiceId) => {
    if (!appointment?.id || !vehicleServiceId) return;
    try {
      const clean = await removeUnnecessaryFields({
        ...appointment,
        vehicleServiceId,
      });
      await putData(`/agenda/${appointment.id}`, clean);
      applyAppointmentLocalUpdate({
        ...appointment,
        vehicleServiceId,
      });
    } catch {
      /* no bloquear apertura del modal */
    }
  };

  const resolveVehicleServiceId = async (appointment) => {
    const linked = Number(appointment?.vehicleServiceId);
    if (Number.isFinite(linked) && linked > 0) return linked;

    const agendaId = Number(appointment?.id);
    if (!Number.isFinite(agendaId) || agendaId <= 0) return null;

    try {
      const response = await getData(`/vehicleServices/${agendaId}`, loaderOpts);
      const resolved = response?.payload?.vehicleService?.id;
      if (resolved) {
        await linkAgendaToVehicleService(appointment, resolved);
        return resolved;
      }
    } catch {
      /* sin servicio gemelo por id */
    }
    return null;
  };

  const getVehicleService = async (vehicleServiceId) => {
    const id = Number(vehicleServiceId);
    if (!Number.isFinite(id) || id <= 0) return null;
    try {
      const response = await getData(`/vehicleServices/${id}`, loaderOpts);
      setVehicleService(response.payload.vehicleService);
      return response.payload.vehicleService;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo obtener el servicio.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  };

  const loadVehicleServiceForAppointment = async (appointment) => {
    if (!appointment?.id) {
      setVehicleService(null);
      return;
    }
    const resolvedId = await resolveVehicleServiceId(appointment);
    if (!resolvedId) {
      setVehicleService(null);
      return;
    }
    await getVehicleService(resolvedId);
  };

  /** Actualiza el servicio en memoria (p. ej. tras asociar recepción) sin PUT. */
  const applyVehicleServiceUpdate = (nextVehicleService) => {
    if (!nextVehicleService) return;
    setVehicleService(nextVehicleService);
  };

  /** Actualiza un turno en el listado local sin volver a guardar en agenda. */
  const applyAppointmentLocalUpdate = (updatedAppointment) => {
    if (!updatedAppointment?.id) return;
    setAppointmentList((prev) =>
      prev.map((appointment) =>
        appointment.id === updatedAppointment.id
          ? { ...appointment, ...updatedAppointment }
          : appointment,
      ),
    );
  };

  // Solo recargar al elegir otro turno (por id), no cuando el modal enriquece el vehículo.
  useEffect(() => {
    loadVehicleServiceForAppointment(selectedEvent?.appointment);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al cambiar de turno
  }, [selectedEvent?.appointment?.id]);

  const removeUnnecessaryFields = async (appointment) => {
    const {
      customer,
      vehicle,
      service,
      mechanic,
      serviceType,
      customerTransfer,
      ...cleanedAppointment
    } = appointment;
    return cleanedAppointment;
  };

  const handleUpdateEvent = async (updatedAppointment) => {
    try {
      const cleanAppointment = await removeUnnecessaryFields(updatedAppointment);
      await putAppointment(cleanAppointment);

      setAppointmentList((prev) =>
        prev.map((appointment) =>
          appointment.id === updatedAppointment.id
            ? updatedAppointment
            : appointment,
        ),
      );
    } catch (error) {
      /* handled in put */
    }
  };

  return (
    <AdminPageShell title="Calendario de agenda" fullHeight>
      <Box flex="1" minH={0} h="100%" px={{ base: 2, md: 3 }} pb={{ base: 2, md: 3 }}>
        <SchedulerDetail
          appointmentList={appointmentList}
          holidayList={holidayList}
          handleUpdateEvent={handleUpdateEvent}
          vehicleService={vehicleService}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          putVehicleService={putVehicleService}
          putDynamicTest={putDynamicTest}
          productsList={productsList}
          putVehicleData={putVehicleData}
          statusList={statusList}
          mechanicsList={mechanicsList}
          vehicleIdsWithAlerts={vehicleIdsWithAlerts}
          onAlertsChanged={loadVehicleAlerts}
          onAppointmentsRefresh={refreshAppointments}
          onAppointmentRangeChange={handleAppointmentRangeChange}
          onVehicleServiceUpdated={applyVehicleServiceUpdate}
          onAppointmentLocalUpdate={applyAppointmentLocalUpdate}
        />
      </Box>
    </AdminPageShell>
  );
};

export default Scheduler;
